import { Dispatch } from "redux";
import { loadAndTrack } from "redux-request-loading";

import { Payload } from "@neworbit/redux-helpers";

import { questionnaireLoaded, saveQuestionnaireSuccess } from "@teamprevent/common/questionnaires/actions";
import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import { PHYSIOTHERAPY_REFERRAL_REPORT_KEY } from "@teamprevent/common/physiotherapyReferralReport/models";

import { AddTaskModel, TasksUpdatedSuccessResult, CreateTaskDto } from "../task/model";
import { consultationLoaded } from "../consultations/actions";
import { actionTaskSuccess } from "../crud/ohCase/actions";

import {
    LOAD_PHYSIOTHERAPY_REFERRAL_REPORT,
    SAVE_PHYSIOTHERAPY_REFERRAL_REPORT,
    SUBMIT_PHYSIOTHERAPY_REFERRAL_REPORT,
    LOAD_CONSULTATION_AND_PHYSIOTHERAPY_REFERRAL_DETAILS,
    APPROVE_PHYSIOTHERAPY_REFERRAL_REPORT,
    APPROVE_PHYSIOTHERAPY_REFERRAL_REPORT_SUCCESS,
    REQUEST_PHYSIOTHERAPY_CHANGES_REFERRAL_REPORT,
    REQUEST_PHYSIOTHERAPY_CHANGES_REFERRAL_REPORT_SUCCESS,
    REJECT_PHYSIOTHERAPY_REFERRAL_REPORT,
    REJECT_PHYSIOTHERAPY_REFERRAL_REPORT_SUCCESS
} from "./actionTypes";
import { physiotherapyReferralReportApi } from "./PhysiotherapyReferralReportApi";

export type ApprovePhysiotherapyReferralReportSuccess =
    ({ type: typeof APPROVE_PHYSIOTHERAPY_REFERRAL_REPORT_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;
export type RequestChangesPhysiotherapyReferralReportSuccess =
    ({ type: typeof REQUEST_PHYSIOTHERAPY_CHANGES_REFERRAL_REPORT_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;
export type RejectPhysiotherapyReferralReportSuccess =
    ({ type: typeof REJECT_PHYSIOTHERAPY_REFERRAL_REPORT_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;

export const loadQuestionnaire = (ohCaseId: string, taskId: string, questionnaireKey: string, omittedSections?: string[]) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, "loadQuestionnaire", physiotherapyReferralReportApi.getReport(ohCaseId, taskId));
        if (response) {
            let questions = response.data?.questions ?? [];
            if (omittedSections && omittedSections.length > 0) {
                questions = questions.filter(q => !omittedSections.some(s => s === q.sectionId));
            }
            dispatch(questionnaireLoaded({
                questions,
                questionnaireKey,
                eTag: response.eTag,
                isDefaultCustomerType: response.isDefaultCustomerType,
                customerType: response.customerType
            }));
        }
        return response;
    };
};

export const getPhysiotherapyReferralReport = (ohCaseId: string, taskId: string, questionnaireKey: string) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(
            dispatch,
            LOAD_PHYSIOTHERAPY_REFERRAL_REPORT,
            physiotherapyReferralReportApi.getReportV2(ohCaseId, taskId)
        );
        if (response) {
            const questions = response.data?.questions ?? [];
            dispatch(questionnaireLoaded({ questions, questionnaireKey, eTag: response.eTag }));
        }
    };
};

export const loadConsultationAndPhysiotherapyReferralDetails = (ohCaseId: string, taskId: string) => {
    return async (dispatch: Dispatch) => {
        const consultationDetails = await loadAndTrack(
            dispatch,
            LOAD_CONSULTATION_AND_PHYSIOTHERAPY_REFERRAL_DETAILS,
            physiotherapyReferralReportApi.getConsultation(ohCaseId, taskId)
        );
        if (consultationDetails) {
            dispatch(consultationLoaded(consultationDetails));
        }
    };
};

export const savePhysiotherapyReferralReport = (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(
            dispatch,
            SAVE_PHYSIOTHERAPY_REFERRAL_REPORT,
            physiotherapyReferralReportApi.saveReport(ohCaseId, taskId, questions, eTag)
        );
        dispatch(saveQuestionnaireSuccess({ questionnaireKey: PHYSIOTHERAPY_REFERRAL_REPORT_KEY, eTag: response.eTag }));
    };
};

export const submitPhysiotherapyReferralReportV2 = (ohCaseId: string, taskId: string, tasks: AddTaskModel[]) => {
    return async (dispatch: Dispatch) => {
        const updatedTasks = await loadAndTrack(
            dispatch,
            SUBMIT_PHYSIOTHERAPY_REFERRAL_REPORT,
            physiotherapyReferralReportApi.submitReportV2(ohCaseId, taskId, tasks)
        );
        dispatch(actionTaskSuccess({ ohCaseId, tasks: updatedTasks || [] }));
    };
};

export const submitPhysiotherapyReferralReport = (ohCaseId: string, taskId: string, tasks: CreateTaskDto[]) => {
    return async (dispatch: Dispatch) => {
        const updatedTasks = await loadAndTrack(
            dispatch,
            SUBMIT_PHYSIOTHERAPY_REFERRAL_REPORT,
            physiotherapyReferralReportApi.submitReport(ohCaseId, taskId, tasks)
        );
        dispatch(actionTaskSuccess({ ohCaseId, tasks: updatedTasks || [] }));
    };
};

export const submitPhysiotherapyReferralReportAmendment = (ohCaseId: string, taskId: string, tasks: AddTaskModel[]) => {
    return async (dispatch: Dispatch) => {
        const updatedTasks = await loadAndTrack(
            dispatch,
            SUBMIT_PHYSIOTHERAPY_REFERRAL_REPORT,
            physiotherapyReferralReportApi.submitReportAmendment(ohCaseId, taskId, tasks)
        );
        dispatch(actionTaskSuccess({ ohCaseId, tasks: updatedTasks || [] }));
    };
};

export const approvePhysiotherapyReferralReport = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const results = await loadAndTrack(
            dispatch,
            APPROVE_PHYSIOTHERAPY_REFERRAL_REPORT,
            physiotherapyReferralReportApi.approve(ohCaseId, taskId, notes)
        );
        dispatch(approvePhysiotherapyReferralReportSuccess({ ohCaseId, tasks: results || [] }));
        dispatch(actionTaskSuccess({ ohCaseId, tasks: results || [] }));
    };
};

export const requestChangesPhysiotherapyReferralReport = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const results = await loadAndTrack(
            dispatch,
            REQUEST_PHYSIOTHERAPY_CHANGES_REFERRAL_REPORT,
            physiotherapyReferralReportApi.requestChanges(ohCaseId, taskId, notes)
        );
        dispatch(requestPhysiotherapyChangesReferralReportSuccess({ ohCaseId, tasks: results || [] }));
        dispatch(actionTaskSuccess({ ohCaseId, tasks: results || [] }));
    };
};

export const rejectPhysiotherapyReferralReport = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const results = await loadAndTrack(
            dispatch,
            REJECT_PHYSIOTHERAPY_REFERRAL_REPORT,
            physiotherapyReferralReportApi.reject(ohCaseId, taskId, notes)
        );
        dispatch(rejectPhysiotherapyReferralReportSuccess({ ohCaseId, tasks: results || [] }));
        dispatch(actionTaskSuccess({ ohCaseId, tasks: results || [] }));
    };
};

export const approvePhysiotherapyReferralReportSuccess = (payload: TasksUpdatedSuccessResult): ApprovePhysiotherapyReferralReportSuccess => {
    return {
        type: APPROVE_PHYSIOTHERAPY_REFERRAL_REPORT_SUCCESS,
        payload
    };
};

export const requestPhysiotherapyChangesReferralReportSuccess = (payload: TasksUpdatedSuccessResult): RequestChangesPhysiotherapyReferralReportSuccess => {
    return {
        type: REQUEST_PHYSIOTHERAPY_CHANGES_REFERRAL_REPORT_SUCCESS,
        payload
    };
};

export const rejectPhysiotherapyReferralReportSuccess = (payload: TasksUpdatedSuccessResult): RejectPhysiotherapyReferralReportSuccess => {
    return {
        type: REJECT_PHYSIOTHERAPY_REFERRAL_REPORT_SUCCESS,
        payload
    };
};
